import { trpc } from "../trpc";
import { ListChatbotsOutput } from "@zapier/bff";

export type SearchChatbotsCache = ListChatbotsOutput | undefined;

function addChatbotToSearchResults(
  cache: SearchChatbotsCache,
  newChatbot: NonNullable<ListChatbotsOutput>["data"][0],
): SearchChatbotsCache {
  if (!cache) {
    return cache;
  }

  return {
    ...cache,
    data: [newChatbot, ...cache.data],
  };
}

interface Input {
  folderId?: string;
}

export function useDuplicateChatbot(input?: Input) {
  const { folderId } = input || {};
  const utils = trpc.useUtils();

  return trpc.chatbot.duplicate.useMutation({
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await utils.chatbot.list.cancel();

      // Snapshot the previous value
      const previousChatbots = utils.chatbot.list.getData({});

      // Don't do optimistic update if we're in a folder view (folderId present)
      // because the chatbot won't be in the search results until after the move
      if (!folderId && previousChatbots?.data && variables.chatbotId) {
        // Find the source chatbot to use as template
        const sourceChatbot = previousChatbots.data.find(
          (c) => c.id === variables.chatbotId,
        );

        if (sourceChatbot) {
          // Create optimistic chatbot with the data we know
          const optimisticChatbot = {
            ...sourceChatbot,
            id: `temp-${Date.now()}`, // Temporary ID
            name: sourceChatbot.name,
            createdAt: new Date().toISOString(),
            editedAt: new Date().toISOString(),
          };
          // Optimistically update the cache
          utils.chatbot.list.setData({}, (cache: SearchChatbotsCache) =>
            addChatbotToSearchResults(cache, optimisticChatbot),
          );
        }
      }

      return { previousChatbots };
    },
    onError: (_error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.previousChatbots) {
        utils.chatbot.list.setData({}, context.previousChatbots);
      }
    },
    onSuccess: (response) => {
      // Replace optimistic data with real data
      if (response) {
        utils.chatbot.list.setData({}, (cache: SearchChatbotsCache) => {
          if (!cache) return cache;

          // Remove any temp entries and ensure the real chatbot is at the top
          const filteredData = cache.data.filter(
            (chatbot) => !chatbot.id?.startsWith("temp-"),
          );

          return {
            ...cache,
            data: [response, ...filteredData],
          };
        });
      }
    },
    onSettled: () => {
      utils.chatbot.list.invalidate();
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({
          folderId,
        });
      }
    },
  });
}
